/**
 * Touch control surface — an input-source-agnostic on-screen control cluster: a
 * ◀ ▶ d-pad (bottom-left) plus the bottom-right ACTION cluster (Release · Jump ·
 * Skill/Absorb), the owner-tuned prototype `GameScene` cluster (`ui.jsx:366`,
 * `screens3.jsx:230-251`). It NEVER imports Colyseus and NEVER sends on the wire:
 * the d-pad/jump only mutate a caller-supplied `TouchInputState` that the host
 * scene ORs into the same `InputState` the keyboard fills, and the Skill/Release
 * buttons call the caller-supplied `onGrab`/`onRelease` edge hooks — the host
 * fires the EXISTING `ClientMessage.Grab`/`.Release` (no new wire surface, no
 * shared/server change; same intents the E/R keys already produce).
 *
 * The Skill (Absorb) + Release buttons are the player-control-kit piece touch
 * players were missing — the co-op action verb (absorb a teammate into the
 * bridge-chain / grab a block, and drop). `onGrab`/`onRelease` are wired by the
 * stage host; the lobby leaves them undefined (no action buttons there).
 */

/** Polled (edge-less) press state, OR-ed into the host's `InputState` each frame. */
export interface TouchInputState {
  left: boolean;
  right: boolean;
  /**
   * High for exactly one host poll after a Jump press (mirrors the prototype's
   * `onDown=doJump` edge + the keyboard send-on-change jump edge). The host reads
   * it, sends, and calls `clearJumpEdge()` so the next poll is low again — the
   * server still gates the actual jump on `grounded` (no client authority).
   */
  jump: boolean;
}

export interface TouchControlsOptions {
  /** The scene's mounted DOM overlay root (a `.pp-root`-anchored element). */
  root: HTMLElement;
  /** Prototype "Bigger touch controls" scale (`aS = big ? 1.16 : 1`). Default 1. */
  big?: boolean;
  /** Optional hint bar text (e.g. the prototype's "◀ ▶ run · ⤒ jump"). */
  hint?: string;
  /** Edge callback on Jump press (host may leave `state.jump` high one tick). */
  onJump?: () => void;
  /**
   * Press (pointer-DOWN) callback on Skill/Absorb (the co-op grab/absorb verb).
   * When set, the bottom-right action cluster renders the Skill button. Absorb is
   * a HOLD-to-channel skill, so this fires on press and `onGrabEnd` on release.
   */
  onGrab?: () => void;
  /** Release (pointer-UP) callback for the held Skill/Absorb channel. */
  onGrabEnd?: () => void;
  /**
   * Edge callback on Release press (drop the held thing). When set, the action
   * cluster renders the Release button; the host fires `ClientMessage.Release`.
   */
  onRelease?: () => void;
}

export interface TouchControlsHandle {
  /** Polled by the host each frame; `jump` is a one-poll edge (see above). */
  state: TouchInputState;
  /**
   * The bottom-right action row (`.tc-actions`). Prepend the EmoteWheel element
   * here so the kit reads as the prototype's single cluster
   * (EmoteWheel · Release · Jump · Skill, left→right; `screens3.jsx:237-251`).
   */
  actionCluster: HTMLElement;
  /** Host calls this after consuming a jump edge so the next poll reads low. */
  clearJumpEdge(): void;
  destroy(): void;
}

/**
 * True on coarse / touch-capable pointers (per the contract). Keyboard always
 * stays live, so a hybrid laptop/tablet gets both; a fine-pointer desktop gets
 * NO overlay (zero regression).
 */
export function isCoarsePointer(): boolean {
  return (
    window.matchMedia?.('(pointer: coarse)').matches ||
    window.matchMedia?.('(hover: none)').matches ||
    'ontouchstart' in window ||
    (navigator.maxTouchPoints ?? 0) > 0
  );
}

/**
 * PonPonLand is a mobile-first game, so the on-screen control kit is shown
 * ALWAYS — on desktop too (so it's testable in a desktop browser) — with the
 * keyboard staying live alongside. Flip `ALWAYS_SHOW_CONTROLS` to false to
 * revert to touch-only auto-detect (`isCoarsePointer`).
 */
const ALWAYS_SHOW_CONTROLS = true;
export function showOnScreenControls(): boolean {
  return ALWAYS_SHOW_CONTROLS || isCoarsePointer();
}

/** A press-and-hold button (prototype `HoldButton`, `ui.jsx:366`). */
function holdButton(
  label: string,
  className: string,
  size: number,
  onDown: () => void,
  onUp: () => void,
): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = className;
  b.style.width = `${size}px`;
  b.style.height = `${size}px`;
  b.innerHTML = label;
  const up = (e?: PointerEvent): void => {
    if (e) e.preventDefault();
    onUp();
  };
  b.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    try {
      b.setPointerCapture(e.pointerId);
    } catch {
      /* setPointerCapture may throw on some pointer ids; safe to ignore */
    }
    onDown();
  });
  b.addEventListener('pointerup', up);
  b.addEventListener('pointercancel', up);
  b.addEventListener('pointerleave', up);
  return b;
}

/**
 * A tap action button (prototype `.act`, `ponpon.css:184`) — fires `onTap` on
 * the pointer DOWN edge, matching the keyboard's `keyGrab/keyRelease.on('down')`
 * edge and the prototype's `skillDown` onPointerDown trigger. The press/release
 * look is the `.act:active` CSS; no held state is tracked (Grab/Release are
 * independent edge intents the server resolves per tick).
 */
function actButton(
  innerHTML: string,
  className: string,
  size: number,
  onTap: () => void,
): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = className;
  b.style.width = `${size}px`;
  b.style.height = `${size}px`;
  b.innerHTML = innerHTML;
  b.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    onTap();
  });
  return b;
}

/**
 * Mount the kit over the given root: the ◀ ▶ d-pad (bottom-left) + the bottom-right
 * action cluster (Release · Jump · Skill/Absorb; Release/Skill only when the host
 * wires `onRelease`/`onGrab`). The d-pad/Jump mutate the returned `state` (no wire
 * send); Skill/Release call the `onGrab`/`onRelease` edge hooks. The host inserts
 * the EmoteWheel as the leftmost item of `actionCluster`. Returns `destroy()` for
 * scene shutdown.
 */
export function mountTouchControls(opts: TouchControlsOptions): TouchControlsHandle {
  const state: TouchInputState = { left: false, right: false, jump: false };
  const aS = opts.big ? 1.16 : 1;
  const dpadSize = Math.round(64 * aS);
  const jumpSize = Math.round(66 * aS);
  const actSize = Math.round(46 * aS);
  const skillSize = Math.round(76 * aS);

  // bottom-left: ◀ ▶ (prototype `screens3.jsx:230-233`).
  const moveCluster = document.createElement('div');
  moveCluster.className = 'tc-cluster tc-move';
  const left = holdButton(
    '◀',
    'ctrl',
    dpadSize,
    () => {
      state.left = true;
    },
    () => {
      state.left = false;
    },
  );
  const right = holdButton(
    '▶',
    'ctrl',
    dpadSize,
    () => {
      state.right = true;
    },
    () => {
      state.right = false;
    },
  );
  moveCluster.append(left, right);

  // bottom-right: the action cluster — EmoteWheel slot (host-inserted, leftmost) ·
  // Release · Jump · Skill (prototype `screens3.jsx:237-251`). align flex-end so
  // the small Release/Emote sit on the same baseline as the big Jump/Skill.
  const actionCluster = document.createElement('div');
  actionCluster.className = 'tc-cluster tc-actions';

  // Release (drop) — the smaller pink `.act` (`screens3.jsx:241`). Edge tap →
  // existing `ClientMessage.Release`. Only present when the host wires onRelease.
  if (opts.onRelease) {
    const release = actButton(
      'Release',
      'act tc-release',
      actSize,
      () => opts.onRelease?.(),
    );
    actionCluster.appendChild(release);
  }

  // ⤒ Jump (prototype `screens3.jsx:242`).
  const jump = holdButton(
    '⤒<span class="tc-sub">Jump</span>',
    'ctrl jump',
    jumpSize,
    () => {
      state.jump = true;
      opts.onJump?.();
    },
    () => {
      /* jump is an edge, cleared by the host via clearJumpEdge() */
    },
  );
  actionCluster.appendChild(jump);

  // Skill / Absorb — the big green `.act` (`screens3.jsx:243-250`). Edge tap →
  // existing `ClientMessage.Grab` (the co-op absorb/link/grab verb). Section 1 has
  // no elements, so the verb is "Absorb" (grab a teammate into the bridge-chain /
  // grab a block); the server decides the outcome. Only present when onGrab wired.
  if (opts.onGrab) {
    // HOLD-to-channel: onGrab on press, onGrabEnd on release (the absorb is held).
    // Label is the verb "Absorb" (the retired "grab" subtitle is gone).
    const skill = holdButton(
      'Absorb',
      'act tc-skill',
      skillSize,
      () => opts.onGrab?.(),
      () => opts.onGrabEnd?.(),
    );
    actionCluster.appendChild(skill);
  }

  opts.root.append(moveCluster, actionCluster);

  let hintEl: HTMLDivElement | null = null;
  if (opts.hint) {
    hintEl = document.createElement('div');
    hintEl.className = 'tc-hint';
    hintEl.textContent = opts.hint;
    opts.root.appendChild(hintEl);
  }

  return {
    state,
    actionCluster,
    clearJumpEdge(): void {
      state.jump = false;
    },
    destroy(): void {
      moveCluster.remove();
      actionCluster.remove();
      hintEl?.remove();
    },
  };
}
